// feat/buyer-inventory-ui (2026-05-26) — 업체 전체 매물 마켓플레이스 (M2)
// 차량별 "문의하기" → 온플랫폼 inquiry (탈중개 방어). 백엔드: /api/buyer-app/inventory (M1).
// 2026-05-26 후속 #2+#3 — 카드 그리드 → 테이블 + 광고가 KRW+USD 병기 (locale 보조통화).
//   row 클릭 → /buyer/inventory/:id 상세. 가격 = formatKrwAmountWithConvert (buyer 앱 공통 패턴).

import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import { useNavigate } from 'react-router-dom';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import AuthImage from './AuthImage';
import EmptyState from './EmptyState';
import InventoryInquiryModal from './InventoryInquiryModal';
import { useT } from '../../i18n/I18nProvider';
import { formatKrwAmountWithUsd, getUsdKrwRate, useExchangeRates } from '../../i18n/currency';
import TrustSpeedBadge from '../TrustSpeedBadge';
import { gradeI18nKey } from '../../utils/trustSpeedGrades';

var SORT_OPTIONS = [
  { key: 'latest',       labelKey: 'invSortLatest' },
  { key: 'price_asc',    labelKey: 'invSortPriceLow' },
  { key: 'price_desc',   labelKey: 'invSortPriceHigh' },
  { key: 'mileage_asc',  labelKey: 'invSortMileageLow' },
  { key: 'mileage_desc', labelKey: 'invSortMileageHigh' },
];

// 검차 배지 — summaryKey(백엔드) → i18n 키. 색은 inspection.summaryColor 사용.
var INSPECT_SUMMARY_I18N = {
  accidentFree: 'invInspectAccidentFree',
  minorAccident: 'invInspectMinorAccident',
  accident: 'invInspectAccident'
};

// list 카드 검차 칩 (있을 때만). 색=summaryColor, 라벨=i18n. 🔍 prefix.
function InspectChip(props) {
  var insp = props.car.inspection;
  if (!insp || !insp.hasInspection) return null;
  return (
    <span style={{ display: 'inline-block', marginTop: 3, padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700,
      color: '#fff', background: insp.summaryColor || '#94a3b8' }}>
      🔍 {props.t(INSPECT_SUMMARY_I18N[insp.summaryKey] || 'invInspectTitle')}
    </span>
  );
}

// 판매자 신뢰 라인 — 회사명 + 신뢰속도 등급 배지 + 누적 거래량 (당근마켓 매너온도 대체).
//   거래량 = "거래량 많은 우량 업체" 신호 (금액 미노출). companyId=0 orphan 은 grade null → 배지 생략.
function SellerTrust(props) {
  var seller = props.car.seller, t = props.t;
  if (!seller) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span>🏢 {seller.companyNameEn || seller.companyName}</span>
      {seller.trustSpeedGrade
        ? <TrustSpeedBadge grade={seller.trustSpeedGrade} label={t(gradeI18nKey(seller.trustSpeedGrade))} size="sm" />
        : null}
      {seller.completedDeals > 0
        ? <span style={{ color: '#64748b', fontWeight: 700 }}>{t('completedDealsCount', { count: seller.completedDeals })}</span>
        : null}
    </span>
  );
}

// 좁은 화면(폰) 감지 — 테이블 가로스크롤 대신 카드 스택
function useIsMobile(bp) {
  var b = bp || 720;
  var hook = useState(typeof window !== 'undefined' && window.innerWidth <= b);
  var m = hook[0], setM = hook[1];
  useEffect(function() {
    var h = function(){ setM(window.innerWidth <= b); };
    window.addEventListener('resize', h);
    return function(){ window.removeEventListener('resize', h); };
  }, [b]);
  return m;
}

function specText(car) {
  var parts = [];
  if (car.mileage) parts.push(Number(car.mileage).toLocaleString('en-US') + ' km');
  if (car.fuel) parts.push(car.fuel);
  if (car.color) parts.push(car.color);
  if (car.regDate) parts.push(String(car.regDate).slice(0, 7));
  return parts.join(' · ');
}

// ─── 모바일 매물 카드 ───
function InventoryMobileCard(props) {
  var car = props.car, t = props.t, rates = props.rates;
  return (
    <div onClick={function(){ props.onOpen(car); }}
      style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', cursor: 'pointer' }}>
      <div style={{ width: '100%', height: 160, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {car.coverImageUrl
          ? <AuthImage path={car.coverImageUrl} alt={car.carType} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <span style={{ fontSize: 40, color: '#cbd5e1' }}>🚗</span>}
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{car.carType || '-'}</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{specText(car) || ''}</div>
        <InspectChip car={car} t={t} />
        <div style={{ marginTop: 8 }}>
          {car.priceKrw != null
            ? <span style={{ fontSize: 15, fontWeight: 800, color: '#1B3A5C' }}>{formatKrwAmountWithUsd(car.priceKrw, t, rates)}</span>
            : <span style={{ color: '#0ea5e9', fontSize: 14 }}>{t('invPriceNegotiable')}</span>}
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
          <SellerTrust car={car} t={t} />
        </div>
        <button type="button" onClick={function(e){ e.stopPropagation(); props.onContact(car); }}
          style={{ marginTop: 10, width: '100%', height: 40, border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#1B3A5C 0%,#3b82f6 100%)', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {t('invContactCta')}
        </button>
      </div>
    </div>
  );
}

// ─── 매물 테이블 행 ───
function InventoryRow(props) {
  var car = props.car;
  var t = props.t;
  var rates = props.rates;

  var specParts = [];
  if (car.mileage) specParts.push(Number(car.mileage).toLocaleString('en-US') + ' km');
  if (car.fuel) specParts.push(car.fuel);
  if (car.color) specParts.push(car.color);
  if (car.regDate) specParts.push(String(car.regDate).slice(0, 7));

  return (
    <tr onClick={function(){ props.onOpen(car); }}
      style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
      <td style={{ padding: 8, width: 92 }}>
        <div style={{ width: 76, height: 56, borderRadius: 8, overflow: 'hidden', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {car.coverImageUrl
            ? <AuthImage path={car.coverImageUrl} alt={car.carType} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <span style={{ fontSize: 26, color: '#cbd5e1' }}>🚗</span>}
        </div>
      </td>
      <td style={{ padding: '8px 10px', minWidth: 140 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{car.carType || '-'}</div>
        <InspectChip car={car} t={t} />
      </td>
      <td style={{ padding: '8px 10px', minWidth: 150, fontSize: 13, color: '#64748b' }}>
        {specParts.join(' · ') || '-'}
      </td>
      <td style={{ padding: '8px 10px', minWidth: 150, whiteSpace: 'normal' }}>
        {car.priceKrw != null
          ? <span style={{ fontSize: 14, fontWeight: 800, color: '#1B3A5C' }}>{formatKrwAmountWithUsd(car.priceKrw, t, rates)}</span>
          : <span style={{ color: '#0ea5e9', fontSize: 13 }}>{t('invPriceNegotiable')}</span>}
      </td>
      <td style={{ padding: '8px 10px', minWidth: 130, fontSize: 12, color: '#94a3b8' }}>
        <SellerTrust car={car} t={t} />
      </td>
      <td style={{ padding: '8px 10px', width: 110, textAlign: 'right' }} onClick={function(e){ e.stopPropagation(); }}>
        <button type="button" onClick={function(){ props.onContact(car); }}
          style={{ height: 34, padding: '0 14px', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#1B3A5C 0%,#3b82f6 100%)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          {t('invContactCta')}
        </button>
      </td>
    </tr>
  );
}

function AuctionBuyerInventoryList() {
  var navigate = useNavigate();
  var t = useT();
  var rateState = useExchangeRates();
  var isMobile = useIsMobile();
  var [items, setItems] = useState([]);
  var [companies, setCompanies] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [page, setPage] = useState(1);
  var [totalPages, setTotalPages] = useState(1);
  var [sort, setSort] = useState('latest');
  var [companyId, setCompanyId] = useState('');
  var [q, setQ] = useState('');
  var [debouncedQ, setDebouncedQ] = useState('');
  var [contactCar, setContactCar] = useState(null);
  var [fuels, setFuels] = useState([]);
  var [fuel, setFuel] = useState('');
  var [minMan, setMinMan] = useState('');   // 가격 필터 입력(만원)
  var [maxMan, setMaxMan] = useState('');
  var [inspectedOnly, setInspectedOnly] = useState(false);   // 검차완료만

  useEffect(function() {
    var h = setTimeout(function(){ setDebouncedQ(q); setPage(1); }, 300);
    return function(){ clearTimeout(h); };
  }, [q]);

  // 업체/연료 필터 드롭다운 (1회 로드)
  useEffect(function() {
    authBuyerFetch(API + '/buyer-app/inventory/companies', {})
      .then(function(r){ return r.json(); })
      .then(function(d){ if (d && d.success) setCompanies(d.data || []); })
      .catch(function(){ /* 드롭다운 없이도 동작 */ });
    authBuyerFetch(API + '/buyer-app/inventory/fuels', {})
      .then(function(r){ return r.json(); })
      .then(function(d){ if (d && d.success) setFuels(d.data || []); })
      .catch(function(){ /* 연료 필터 없이도 동작 */ });
  }, []);

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    var params = new URLSearchParams({ sort: sort, page: String(page), limit: '20' });
    if (companyId) params.append('companyId', companyId);
    if (debouncedQ.trim()) params.append('q', debouncedQ.trim());
    if (fuel) params.append('fuel', fuel);
    var minWon = parseInt(String(minMan).replace(/[^0-9]/g, ''), 10);
    var maxWon = parseInt(String(maxMan).replace(/[^0-9]/g, ''), 10);
    if (minWon > 0) params.append('minPrice', String(minWon * 10000));
    if (maxWon > 0) params.append('maxPrice', String(maxWon * 10000));
    if (inspectedOnly) params.append('inspectedOnly', '1');

    authBuyerFetch(API + '/buyer-app/inventory/list?' + params.toString(), {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data) {
          setItems(d.data.items || []);
          setTotalPages(d.data.totalPages || 1);
        } else {
          setError(displayApiError(d, t, 'invListError'));
        }
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError(t('commonNetwork'));
        setLoading(false);
      });
  }, [sort, page, companyId, debouncedQ, fuel, minMan, maxMan, inspectedOnly, navigate, t]);

  useEffect(function() { load(); }, [load]);

  var thStyle = { padding: '10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#94a3b8', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' };
  var fxRate = getUsdKrwRate(rateState.rates);   // 1 USD = N KRW (오늘 기준환율 표시)

  return (
    <AuctionBuyerLayout activeKey="inventory">
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 32px 80px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{t('invTitle')}</h2>
            {fxRate != null && (
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                {t('invFxRateToday', { rate: fxRate.toLocaleString() })}
                {rateState.isStale ? <span style={{ marginLeft: 6, color: '#f59e0b' }}>{t('currencyStaleMarker')}</span> : null}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={companyId} onChange={function(e){ setCompanyId(e.target.value); setPage(1); }}
              style={{ height: 36, padding: '0 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
              <option value="">{t('invFilterAllCompanies')}</option>
              {companies.map(function(c){ return <option key={c.companyId} value={c.companyId}>{(c.nameEn || c.name) + ' (' + c.count + ')'}</option>; })}
            </select>
            {fuels.length > 0 && (
              <select value={fuel} onChange={function(e){ setFuel(e.target.value); setPage(1); }}
                style={{ height: 36, padding: '0 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
                <option value="">{t('invFilterAllFuels')}</option>
                {fuels.map(function(f){ return <option key={f.fuel} value={f.fuel}>{f.fuel + ' (' + f.count + ')'}</option>; })}
              </select>
            )}
            <select value={sort} onChange={function(e){ setSort(e.target.value); setPage(1); }}
              style={{ height: 36, padding: '0 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
              {SORT_OPTIONS.map(function(o){ return <option key={o.key} value={o.key}>{t(o.labelKey)}</option>; })}
            </select>
            {/* 검차완료만 토글 */}
            <button type="button" onClick={function(){ setInspectedOnly(!inspectedOnly); setPage(1); }}
              style={{ height: 36, padding: '0 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                border: '1.5px solid ' + (inspectedOnly ? '#1B3A5C' : '#e2e8f0'),
                background: inspectedOnly ? '#1B3A5C' : 'white', color: inspectedOnly ? 'white' : '#475569' }}>
              🔍 {t('invFilterInspectedOnly')}
            </button>
            {/* 가격대 필터 (만원, KRW) */}
            <input type="text" inputMode="numeric" value={minMan} onChange={function(e){ setMinMan(e.target.value.replace(/[^0-9]/g, '')); setPage(1); }}
              placeholder={t('invFilterMinPrice')} title={t('invFilterPriceUnit')}
              style={{ height: 36, padding: '0 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white', width: 84 }} />
            <span style={{ color: '#94a3b8' }}>~</span>
            <input type="text" inputMode="numeric" value={maxMan} onChange={function(e){ setMaxMan(e.target.value.replace(/[^0-9]/g, '')); setPage(1); }}
              placeholder={t('invFilterMaxPrice')} title={t('invFilterPriceUnit')}
              style={{ height: 36, padding: '0 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white', width: 84 }} />
            <input type="text" value={q} onChange={function(e){ setQ(e.target.value); }}
              placeholder={t('searchPlaceholder')}
              style={{ height: 36, padding: '0 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white', width: 180 }} />
          </div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 14, marginBottom: 20, color: '#991b1b', fontSize: 14 }}>⚠ {error}</div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>{t('commonLoading')}</div>
        ) : items.length === 0 ? (
          <EmptyState icon="🚗" title={t('invEmpty')} hint={t('invEmptyHint')} />
        ) : (
          <>
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {items.map(function(car) {
                  return <InventoryMobileCard key={car.id} car={car} t={t} rates={rateState.rates}
                    onOpen={function(c){ navigate('/buyer/inventory/' + c.id); }}
                    onContact={function(c){ setContactCar(c); }} />;
                })}
              </div>
            ) : (
            <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                <thead>
                  <tr>
                    <th style={Object.assign({}, thStyle, { width: 92 })}></th>
                    <th style={thStyle}>{t('invColVehicle')}</th>
                    <th style={thStyle}>{t('invColSpec')}</th>
                    <th style={thStyle}>{t('invColPrice')}</th>
                    <th style={thStyle}>{t('invColSeller')}</th>
                    <th style={Object.assign({}, thStyle, { textAlign: 'right' })}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(function(car) {
                    return <InventoryRow key={car.id} car={car} t={t} rates={rateState.rates}
                      onOpen={function(c){ navigate('/buyer/inventory/' + c.id); }}
                      onContact={function(c){ setContactCar(c); }} />;
                  })}
                </tbody>
              </table>
            </div>
            )}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 32 }}>
                <button type="button" disabled={page <= 1} onClick={function(){ setPage(page - 1); }}
                  style={{ height: 36, padding: '0 16px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, color: '#475569', opacity: page <= 1 ? 0.4 : 1, cursor: page <= 1 ? 'not-allowed' : 'pointer' }}>
                  {t('paginationPrev')}
                </button>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#475569', padding: '0 12px' }}>{page} / {totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={function(){ setPage(page + 1); }}
                  style={{ height: 36, padding: '0 16px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, color: '#475569', opacity: page >= totalPages ? 0.4 : 1, cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}>
                  {t('paginationNext')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {contactCar && <InventoryInquiryModal car={contactCar} t={t} navigate={navigate} onClose={function(){ setContactCar(null); }} />}
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerInventoryList;
